import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Link } from 'react-router-dom';
import CircleIcon from '@mui/icons-material/Circle';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Tab } from '@mui/material';
import { Box } from '@mui/material';
import { Button, TextField } from '@mui/material';
import { Grid } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import CatalogAPI from "api/faber/CatalogAPI";
import ColorBox from 'common/Box/ColorWhiteBox';
import PageLayoutV2 from 'common/Layout/Page/PageLayoutV2';
import LayoutSkeleton from "common/Skeletons/LayoutSkeleton";
import NormalText from "common/Typography/NormalText";
import OrbitSession from "hooks/useCookies";

import AddCatalogDialogV2 from "./components/AddCatalogDialogV2";
import AddPackageDialog from "./components/AddPackageDialog";
import AddPageDialog from "./components/AddPageDialog";
import CatalogPriceTab from "./components/CatalogPriceTab";
import EditCatalogDialog from "./components/EditCatalogDialog";
import ManagePageAddCatalog from "./components/ManagePageAddCatalog";
import ManagePageCatalog from "./components/ManagePageCatalog";
function FaberCatalogueDashboardV2() {
  const faberToken = OrbitSession.get("faberToken");
  const [catalogRows, setCatalogRows] = useState<GridRowDataType[]>([]);
  const [catalogSearchInput, setCatalogSearchInput] = useState('');
  const [isCatalogSearchLoading, setIsCatalogSearchLoading] = useState(false);
  const [isFlushingCache, setIsFlushingCache] = useState(false);
  const [flushCacheStatus, setFlushCacheStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const cacheFlushPrefix = process.env.REACT_APP_CATALOG_CACHE_FLUSH_PREFIX || '*';
  interface GridRowDataType {
    id: string;
    catalogId?: string;
    name: string;
    dataInGB: number;
    provisionMethod?: string;
    validityInDays: number;
    voiceInMins?: number;
    visibility?: boolean;
    isVisible?: boolean;
    active?: boolean;
    status?: string;
    showClient?: boolean;
  }

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 220,
      filterable: true,
    },
    {
      field: "name",
      headerName: "Name",
      width: 230,
      filterable: true,
    },
    {
      field: "catalogId",
      headerName: "Catalog ID",
      width: 220,
      valueGetter: (params) => params.row.catalogId || params.row.id,
    },
    { field: "dataInGB", headerName: "Data In GB", width: 120 },
    {
      field: "validityInDays",
      headerName: "Validity",
      width: 110,
    },
    {
      field: "activeState",
      headerName: "Visibility",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GridRowDataType>) => {
        const { row } = params;
        const isActive =
          typeof row.visibility === 'boolean'
            ? row.visibility
            : typeof row.isVisible === 'boolean'
            ? row.isVisible
            : typeof row.active === 'boolean'
            ? row.active
            : typeof row.showClient === 'boolean'
            ? row.showClient
            : typeof row.status === 'string'
            ? row.status.toLowerCase() === 'active'
            : false;

        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <CircleIcon sx={{ color: isActive ? 'success.main' : 'error.main', fontSize: 12 }} />
            <Box component="span">{isActive ? 'Active' : 'Inactive'}</Box>
          </Box>
        );
      },
    },
    {
      field: "voiceInMins",
      headerName: "Voice In Mins",
      width: 130,
    },
    {
      field: "provisionMethod",
      headerName: "Provision Method",
      width: 220,
      valueGetter: (params) => params.row.provisionMethod || '-',
    },
    {
      field: "editCatalog",
      headerName: "Edit",
      width: 110,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<GridRowDataType>) => {
        const { row } = params;
        return <EditCatalogDialog catalogId={String(row.catalogId || row.id)} />;
      },
    },
  ];

  interface GridRowPageDataType {
    id: string;
    name: string;
    displayName: string;
    createdAt?: string;
  }
  const pageColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "Page ID",
      width: 300,
      filterable: true,
      sortable: false,
    },
    {
      field: "displayName",
      headerName: "Display Name",
      width: 300,
      filterable: true,
      sortable: true,
    },
    {
      field: "name",
      headerName: "Page Name",
      width: 300,
      filterable: true,
    },

    {
      field: "updatePage",
      flex: 0.75,
      width: 200,
      filterable: false,
      sortable: false,
      headerName: "Catalog info",
      renderCell: (params: { row: GridRowPageDataType }) => {
        const { row } = params;
        return <ManagePageCatalog id={row.id.toString()} pageName={row.name} />;
      },
    },
    {
      field: "addCatalog",
      flex: 0.75,
      width: 200,
      filterable: false,
      sortable: false,
      headerName: "Add Catalog",
      renderCell: (params: GridRenderCellParams<GridRowPageDataType>) => {
        const { row } = params;
        return <ManagePageAddCatalog id={row.id.toString()} pageName={row.name} />;
      },
    },
  ];

  const {
    data: catalogItems,
  } = useQuery(["getCatalogItems"], async () => {
    return CatalogAPI.getAllCatalogItems(faberToken);
  });

  const normalizeCatalogRows = useMemo(
    () => (payload: unknown): GridRowDataType[] => {
      const root = (payload || {}) as {
        data?: unknown;
        body?: { data?: unknown };
      };

      const body = root.data || root.body || root;
      const rawRows = (Array.isArray(body)
        ? body
        : (body as { data?: unknown }).data) || [];

      if (!Array.isArray(rawRows)) {
        return [];
      }

      return rawRows
        .map((row) => {
          const item = row as Record<string, unknown>;
          const id = String(item.id || item.catalogId || item.catalog_id || '');
          if (!id) {
            return null;
          }

          const provisionMethodRaw = item.provisionMethod as unknown;
          const provisionMethod =
            typeof provisionMethodRaw === 'string'
              ? provisionMethodRaw
              : ((provisionMethodRaw as { method?: string })?.method || '');

          return {
            id,
            catalogId: String(item.catalogId || item.catalog_id || id),
            name: String(item.name || ''),
            dataInGB: Number(item.dataInGB || 0),
            provisionMethod,
            validityInDays: Number(item.validityInDays || 0),
            voiceInMins: Number(item.voiceInMins || 0),
            visibility:
              typeof item.visibility === 'boolean' ? item.visibility : undefined,
            isVisible:
              typeof item.isVisible === 'boolean' ? item.isVisible : undefined,
            active: typeof item.active === 'boolean' ? item.active : undefined,
            status: typeof item.status === 'string' ? item.status : undefined,
            showClient:
              typeof item.showClient === 'boolean' ? item.showClient : undefined,
          } as GridRowDataType;
        })
        .filter((row): row is GridRowDataType => Boolean(row));
    },
    []
  );

  useEffect(() => {
    if (catalogItems) {
      setCatalogRows(normalizeCatalogRows(catalogItems));
    }
  }, [catalogItems, normalizeCatalogRows]);

  const handleCatalogSearch = async () => {
    const searchTerm = catalogSearchInput.trim();

    if (!searchTerm) {
      setCatalogRows(normalizeCatalogRows(catalogItems));
      return;
    }

    setIsCatalogSearchLoading(true);
    try {
      const searchResponse = await CatalogAPI.searchCatalogItems(faberToken, searchTerm);
      setCatalogRows(normalizeCatalogRows(searchResponse));
    } catch (error) {
      setCatalogRows([]);
    } finally {
      setIsCatalogSearchLoading(false);
    }
  };

  const [tabValue, setTabValue] = useState('1')
  const [pagesPage, setPagesPage] = useState(0)
  const [pagesLimit, setPagesLimit] = useState(10)

  const {
    data: pages,
    isLoading: isCatalogPagesLoading,
    isFetching: isCatalogPagesFetching,
  } = useQuery(
    ["getCatalogPages", pagesPage, pagesLimit],
    async () => {
      return CatalogAPI.getAllCatalogPages(pagesPage + 1, pagesLimit);
    },
    {
      enabled: tabValue === '2',
    }
  )

  const { catalogPages, catalogPagesTotal } = useMemo(() => {
    const root = pages || {}
    const body = root.data || root.body || root

    const rawRows =
      body.allPacks ||
      body.data ||
      body.destinations ||
      body.items ||
      body.results ||
      []

    const normalizedRows: GridRowPageDataType[] = (Array.isArray(rawRows) ? rawRows : [])
      .map((row: Record<string, unknown>) => {
        const id = String(row.id || row.pageId || row.destinationId || '')
        const name = String(row.name || row.pageName || row.destinationName || '')
        const displayName = String(row.displayName || row.label || name || '')

        if (!id) {
          return null
        }

        return {
          id,
          name,
          displayName,
          createdAt: String(row.createdAt || '')
        } as GridRowPageDataType
      })
      .filter((row: GridRowPageDataType | null): row is GridRowPageDataType => Boolean(row))

    const pagination = body.pagination || body.meta || {}
    const total =
      body.total ||
      body.totalCount ||
      pagination.total ||
      pagination.totalCount ||
      0

    return {
      catalogPages: normalizedRows,
      catalogPagesTotal: Number(total) || 0
    }
  }, [pages])

  const handleTabChange = (event, newNum) => setTabValue(newNum)

  const handleFlushCache = async () => {
    setIsFlushingCache(true);
    setFlushCacheStatus('idle');
    try {
      await CatalogAPI.flushCacheByPrefix(faberToken, cacheFlushPrefix);
      setFlushCacheStatus('success');
    } catch (error) {
      setFlushCacheStatus('error');
    } finally {
      setIsFlushingCache(false);
    }
  }

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <TabContext value={tabValue}>
        <PageLayoutV2 header="Manage Catalog">
          <ColorBox sx={{ minWidth: "1000px" }}>
            <TabList
              onChange={handleTabChange}
              aria-label='lab API tabs example'
            >
              <Tab label='Catalog' value='1' component={Link} to='#campaign' />
              <Tab
                label='Pages'
                value='2'
                component={Link}
                to='#catalogs'
              />
              <Tab
                label='Price'
                value='3'
                component={Link}
                to='#catalogprices'
              />
            </TabList>

            <TabPanel value='1'>
              <ColorBox>
                <Box sx={{ minWidth: "1000px" }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    mt={1}
                    mb={2}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        placeholder="Search catalog name"
                        label="Search Catalog Name"
                        value={catalogSearchInput}
                        onChange={(event) => setCatalogSearchInput(event.target.value)}
                        sx={{ minWidth: 280 }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleCatalogSearch}
                      >
                        Search
                      </Button>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleFlushCache}
                        disabled={isFlushingCache}
                      >
                        {isFlushingCache
                          ? 'Flushing...'
                          : flushCacheStatus === 'success'
                          ? 'Flushed'
                          : flushCacheStatus === 'error'
                          ? 'Retry Flush'
                          : 'Flush Redis'}
                      </Button>
                      <AddPackageDialog />
                      <AddCatalogDialogV2 />
                    </Box>
                  </Box>
                  <NormalText isBold>Catalog Items</NormalText>
                  <Grid item xs={12} height={640}>
                    <DataGrid
                      columns={columns}
                      rows={catalogRows}
                      getRowId={(row) => row.id}
                      rowsPerPageOptions={[10]}
                      pageSize={10}
                      loading={isCatalogSearchLoading}
                    />
                  </Grid>
                </Box>
              </ColorBox>
            </TabPanel>
            <TabPanel value='2'>
              <ColorBox>
                <Box display="flex" flexDirection="row" gap={2}>
                  <AddPageDialog />
                </Box>
                <NormalText isBold>Catalog Pages</NormalText>
                <Grid item xs={12} height={640}>
                <DataGrid
                  columns={pageColumns}
                  rows={catalogPages}
                  rowCount={catalogPagesTotal}
                  getRowId={(row) => row.id}
                  loading={isCatalogPagesLoading || isCatalogPagesFetching}
                  paginationMode='server'
                  page={pagesPage}
                  onPageChange={(newPage) => setPagesPage(newPage)}
                  pageSize={pagesLimit}
                  onPageSizeChange={(newPageSize) => {
                    setPagesLimit(newPageSize)
                    setPagesPage(0)
                  }}
                  disableSelectionOnClick
                  rowsPerPageOptions={[10, 20, 50]}
                />
                </Grid>
              </ColorBox>
            </TabPanel>
            <TabPanel value="3">
              <ColorBox>
                <CatalogPriceTab
                  faberToken={faberToken}
                />
              </ColorBox>
            </TabPanel>
          </ColorBox>
        </PageLayoutV2>
      </TabContext>
    </Suspense>
  );
}

export default FaberCatalogueDashboardV2;
